import { useGlobalModal } from '@/hooks/useGlobalModal';
import GlobalModal from '@components/GlobalModal';
import ModalTitle from '@components/modal/ModalTitle';
import UserNameSpan from '@components/userNameSpan';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Divider, Tab, Typography } from '@mui/material';
import { Suspense, useEffect, useState } from 'react';
import { useTabs } from '../hooks/useTabs';
import { Access, AuthorizationWrapper } from '@features/authorization';

const UserPreferencesModal = () => {
  const { onClose, state, globalModalProps } = useGlobalModal('userPreferencesModal');
  const { _ } = useLingui();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  useEffect(() => {
    setSelectedUserId(state.initialUserId ?? null);
  }, [state.initialUserId, state.open]);

  const tabs = useTabs(selectedUserId);

  const [tab, setTab] = useState(tabs[0].id);

  const handleChangeTab = (_: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'md', fullWidth: true }}>
      <ModalTitle
        onClose={onClose}
        title={<Trans>Preferencias de usuario</Trans>}
        subtitle={
          <UserNameSpan
            userId={selectedUserId ?? undefined}
            variant="subtitle1"
            color="text.secondary"
            lineHeight={1}
          />
        }
      >
        <Box>
          <Suspense>
            <AuthorizationWrapper access={Access.UpdateOtherUsersPreferences} fallback={null}>
              <StandardAutocompleteIdsWithFallback
                dataType="users"
                size="small"
                label={_(msg`Configurar otro usuario`)}
                value={selectedUserId}
                onChange={(_, value) => setSelectedUserId(value)}
                sx={{ width: 300 }}
              />
            </AuthorizationWrapper>
          </Suspense>
        </Box>
      </ModalTitle>
      <Divider />
      <Box display="flex" overflow="hidden" minHeight={400}>
        <TabContext value={tab}>
          <Box
            sx={{
              my: '-1px',
              backgroundColor: '#343c47',
              flexShrink: 0,
              '& .MuiTabs-indicator': {
                width: '100%',
                backgroundColor: 'background.paper',
                borderLeft: '4px solid #eee',
                borderTop: 1,
                borderTopColor: 'divider',
                borderBottom: 1,
                borderBottomColor: 'divider',
                zIndex: 1,
              },
            }}
          >
            <TabList
              sx={{
                height: '100%',
                '& .MuiTabs-flexContainer': {
                  height: '100%',
                },
                '& .MuiButtonBase-root': {
                  flexGrow: 0,
                },
                '& .MuiTabs-flexContainer::after': {
                  content: '""',
                  display: 'block',
                  flexGrow: 1,
                  borderRight: 1,
                  borderRightColor: 'divider',
                },
              }}
              orientation="vertical"
              onChange={handleChangeTab}
              aria-label="Modal tabs"
            >
              {tabs.map(({ label, id }) => (
                <Tab
                  key={id}
                  label={
                    <Typography
                      zIndex={2}
                      variant="h6"
                      fontSize="16px !important"
                      textTransform="unset"
                    >
                      {label}
                    </Typography>
                  }
                  value={id}
                  sx={{
                    flexGrow: 1,
                    alignItems: 'start',
                    maxWidth: 'unset',
                    textTransform: 'unset',
                    borderRight: 1,
                    borderRightColor: 'divider',
                    '&.Mui-selected': {
                      color: 'white',
                    },
                  }}
                />
              ))}
            </TabList>
          </Box>
          {tabs.map(({ id, component }) => (
            <TabPanel sx={{ flexGrow: 1 }} key={id} value={id}>
              {component}
            </TabPanel>
          ))}
        </TabContext>
      </Box>
    </GlobalModal>
  );
};

export default UserPreferencesModal;
